// Mermaid source hardening, applied before Mermaid (securityLevel 'strict', htmlLabels off)
// ever sees the text. Config directives and frontmatter are removed because they can switch
// securityLevel back to 'loose'; click/call/href and styling lines are removed because they
// can navigate, run callbacks or pull url() resources.
import { getPreset } from './tokens.ts'

export const DIAGRAM_TYPES = [
  'flowchart', 'graph', 'sequenceDiagram', 'stateDiagram', 'stateDiagram-v2', 'timeline',
  'journey', 'pie', 'gantt', 'mindmap', 'quadrantChart'
] as const

const STRIP_LINE = /^\s*(click|call|href|style|classDef|linkStyle|class|callback|link|accTitle|accDescr)\b/i
const FORBIDDEN = /(javascript:|data:|vbscript:|url\s*\(|@import|<\s*\/?\s*(script|iframe|object|embed|foreignObject|style|img|a)\b|on\w+\s*=)/i

export interface SanitizedDiagram {
  ok: boolean
  source: string
  type?: string
  reason?: string
  removed: number
}

export function sanitizeMermaid (input: string): SanitizedDiagram {
  if (typeof input !== 'string') return { ok: false, source: '', reason: 'not a string', removed: 0 }
  let text = input.replace(/\r\n?/g, '\n')
  let removed = 0

  if (/^\s*---\s*\n/.test(text)) {
    const end = text.indexOf('\n---', text.indexOf('---') + 3)
    text = end === -1 ? '' : text.slice(end + 4)
    removed++
  }

  const lines = text.split('\n').filter(line => {
    const trimmed = line.trim()
    if (trimmed.startsWith('%%')) { removed++; return false }
    if (STRIP_LINE.test(trimmed)) { removed++; return false }
    return true
  }).map(line => line.replace(/%%\{.*?\}%%/g, () => { removed++; return '' }))

  const source = lines.join('\n').trim()
  if (!source) return { ok: false, source: '', reason: 'empty diagram', removed }
  if (FORBIDDEN.test(source)) return { ok: false, source: '', reason: 'forbidden content', removed }

  const first = (source.split('\n')[0] ?? '').trim().split(/\s+/)[0] ?? ''
  const type = (DIAGRAM_TYPES as readonly string[]).find(t => t.toLowerCase() === first.toLowerCase())
  if (!type) return { ok: false, source: '', reason: `diagram type must be one of ${DIAGRAM_TYPES.join(', ')}`, removed }

  return { ok: true, source, type, removed }
}

/** mermaid.initialize() options for a preset. Plain data, so the export service can pass it into a page. */
export function mermaidConfig (presetId?: string | null): Record<string, unknown> {
  const t = getPreset(presetId).tokens
  return {
    startOnLoad: false,
    securityLevel: 'strict',
    htmlLabels: false,
    flowchart: { htmlLabels: false, curve: 'basis', padding: 12 },
    theme: 'base',
    fontFamily: t['font-body'],
    themeVariables: {
      fontFamily: t['font-body'],
      fontSize: t['text-sm'],
      primaryColor: t['color-accent-soft'],
      primaryBorderColor: t['color-accent'],
      primaryTextColor: t['color-text'],
      secondaryColor: t['color-positive-soft'],
      tertiaryColor: t['color-surface-sunken'],
      lineColor: t['color-border-strong'],
      textColor: t['color-text'],
      mainBkg: t['color-accent-soft'],
      nodeBorder: t['color-accent'],
      clusterBkg: t['color-surface-sunken'],
      clusterBorder: t['color-border'],
      noteBkgColor: t['color-warning-soft'],
      noteBorderColor: t['color-warning']
    }
  }
}

/** DOMPurify options applied to Mermaid's SVG output */
export const DIAGRAM_PURIFY = {
  USE_PROFILES: { svg: true, svgFilters: true },
  FORBID_TAGS: ['foreignObject', 'a', 'script', 'image'],
  FORBID_ATTR: ['href', 'xlink:href']
}
